import React, { useContext } from 'react'

import { Brand } from '@awaze/design-system.components.awaze-theme-provider'
import { IWishlistButtonProps } from '@awaze/design-system.compositions.property-card'
import { Image } from '@awaze/design-system.compositions.property-list/dist/property-list.types'
import type { Deposits } from '@awaze/mfe-common.configs.brand'
import { useIntlContext } from '@awaze/mfe-common.contexts.intl'
import {
  DataLayerEventType,
  DataLayerFactory,
  TmsContext,
} from '@awaze/mfe-common.contexts.tms'
import { CommonSite } from '@awaze/mfe-common.types-and-mocks.site-search-service'
import {
  AccommodationType,
  CommonSearchResult,
  SearchCriteria,
} from '@awaze/mfe-common.types-and-mocks.site-search-service'
import { ImagesItem } from '@awaze/mfe-common.types-and-mocks.site-search-service/types/properties.types'

import { PropertyCardProps } from 'src/__experimental__/PropertyCardNewDesign/PropertyCardNewDesign'
import { OPTIMIZELY_EVENTS, RESULTS_PAGE_SIZE } from 'src/constants'
import { removeFloorplanImagesFromImageArray } from 'src/containers/SearchResults/helpers'
import { formatter } from 'src/containers/SearchResults/propertyFormatter.util'
import { useOptimizelyDecisionsContext } from 'src/context/optimizely-decisions-context'
import { dataLayerErrorHandler } from 'src/utils/dataLayerErrorHandler'
import { trackClientSideOptimizelyEvent } from 'src/utils/optimizely/trackOptimizelyEvent'

import { PropertyCardNewDesign } from '../../__experimental__/PropertyCardNewDesign/PropertyCardNewDesign'
import { PropertyCardSkeleton } from '../../__experimental__/PropertyCardNewDesign/skeleton/PropertyCardSkeleton'

import { StyledPropertyList } from './property-list.style'

export interface PropertyListCompositeProps {
  properties: CommonSite[]
  translations: PropertyCardProps['translations']
  deposits?: Deposits
  config: {
    brand: Brand
    searchCriteria: SearchCriteria
    searchData: CommonSearchResult
    searchParameters: string
    siteSearch: boolean
    datedSearch: boolean
    dateToday: Date
    accommodationType: AccommodationType
    pdpUrlSegment: string
    locale: string
  }
  onCarouselInteractFactory: (
    images: Array<Image | ImagesItem>,
    displayName: string,
  ) => (
    event: 'open' | 'close' | 'navigate',
    index?: number,
    nextOrPrevious?: 'next' | 'previous',
  ) => void
  getWishlistButtonProps?: (code: string | number) => IWishlistButtonProps
}

export const PropertyListComposite = ({
  properties,
  translations,
  deposits,
  config,
  onCarouselInteractFactory,
  getWishlistButtonProps,
}: PropertyListCompositeProps): React.ReactNode => {
  const tms = useContext(TmsContext)
  const optiContext = useOptimizelyDecisionsContext()
  const searchCriteria = config?.searchCriteria
  const searchData = config.searchData
  const searchParameters = new URLSearchParams(config?.searchParameters)
  const datedSearch = searchParameters.has('start')

  const { formatDate, country } = useIntlContext()

  const onPropertyLinkClick = ({ propertyCode }: { propertyCode: string }) => {
    new DataLayerFactory()
      .getEvent(DataLayerEventType.PropertyClick)
      .update(
        tms,
        { propertyCode, searchCriteria, searchData },
        dataLayerErrorHandler,
      )
    trackClientSideOptimizelyEvent(
      OPTIMIZELY_EVENTS.PDP_CLICK_THROUGH,
      optiContext,
    )
  }

  return (
    <StyledPropertyList aria-label="properties">
      {!properties.length &&
        Array(RESULTS_PAGE_SIZE)
          .fill('')
          .map((_, i) => <PropertyCardSkeleton key={i} />)}

      {properties.length > 0 &&
        properties.map((property: CommonSite) => {
          const propertyImagesWithoutFloorplans = property.images
            ? removeFloorplanImagesFromImageArray(property.images)
            : []

          const newProps = formatter({
            property: property,
            brand: config.brand,
            deposits: deposits,
            searchParams: searchParameters,
            datedSearch: datedSearch,
            pdpUrlSegment: config.pdpUrlSegment,
            country,
            formatDate,
          })
          const wishlistButton = getWishlistButtonProps
            ? getWishlistButtonProps(property.code)
            : undefined
          return (
            <PropertyCardNewDesign
              key={newProps.code}
              {...newProps}
              wishlistButton={wishlistButton}
              onPropertyLinkClick={() =>
                onPropertyLinkClick({ propertyCode: property.code })
              }
              translations={translations}
              siteSearch={config.siteSearch}
              datedSearch={config.datedSearch}
              onCarouselInteractCallback={onCarouselInteractFactory(
                propertyImagesWithoutFloorplans,
                property.displayName ?? '',
              )}
            />
          )
        })}
    </StyledPropertyList>
  )
}
